import { useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  SortingState,
  getSortedRowModel,
  ColumnFiltersState,
  getFilteredRowModel,
  VisibilityState,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

import {
  Edit2,
  Trash2,
  Download,
  Search,
  AlertTriangle,
  X,
  Loader2,
} from "lucide-react";
interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  pageIndex: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  renderRowActions?: (row: TData) => React.ReactNode;
  recoveryActions?: (row: TData) => React.ReactNode;
  actionButtons?: React.ReactNode;
  onEdit?: (row: TData) => void;
  onDelete?: (id: string | number) => void;
  onDownload?: (row: TData) => void;
  searchColumns?: string[];
  globalSearch?: string;
  onGlobalSearchChange?: (val: string) => void;
  addButtonLabel?: string;
  validateButtonLabel?: string;
  onValidate?: () => void;
  showAddButton?: boolean;
  onAdd?: () => void;
  onEditClick?: (row: TData) => void;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  pageIndex,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
  onDownload,
  searchColumns,
  globalSearch,
  recoveryActions,
  onGlobalSearchChange,
  addButtonLabel = "Add New",
  validateButtonLabel,
  showAddButton = true,
  isLoading = false,
  onAdd,
  onValidate,
  onEditClick,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [globalFilter, setGlobalFilter] = useState("");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | number | null>(
    null,
  );

  const columnsWithSerialNumber: ColumnDef<TData, any>[] = [
    {
      id: "serial",
      header: () => (
        <div className="w-full text-center font-bold text-black text-lg">
          ID
        </div>
      ),
      cell: (info: any) => (
        <div className="text-center font-bold text-gray-700 text-base">
          {pageIndex * pageSize + info.row.index + 1}
        </div>
      ),
    },
    ...columns,
  ];

  const table = useReactTable({
    data,
    columns: columnsWithSerialNumber,
    enableGlobalFilter: true,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      globalFilter,
    },
    globalFilterFn: (row: any, _columnId: string, filterValue: string) => {
      if (!filterValue) return true;
      if (!searchColumns || searchColumns.length === 0) return true;

      const search = filterValue.toLowerCase();

      return searchColumns.some((colId) => {
        const value = row.getValue(colId);

        if (Array.isArray(value)) {
          return value.some((v) =>
            typeof v === "string"
              ? v.toLowerCase().includes(search)
              : String(v ?? "")
                  .toLowerCase()
                  .includes(search),
          );
        }

        return String(value ?? "")
          .toLowerCase()
          .includes(search);
      });
    },
  });

  const handleDeleteClick = (id: string | number) => {
    if (onDelete) {
      onDelete(id);
    }
  };

  const handleConfirmDelete = () => {
    if (itemToDelete !== null && onDelete) {
      onDelete(itemToDelete);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  return (
    <div className="w-full space-y-4 bg-white p-6 rounded-lg shadow-sm" data-testid="DataTable-root">
      {/* Controls */}
      <div className="flex items-center justify-between gap-4 mb-4" data-testid="DataTable-controls">
        <div className="flex items-center gap-4">
          {showAddButton && onAdd && (
            <Button
              className="bg-[#007bff] text-white hover:bg-blue-700 transition-colors"
              onClick={onAdd}
              data-testid="DataTable-btn-add"
            >
              {addButtonLabel}
            </Button>
          )}

          {onValidate && (
            <Button
              className="bg-[#011934] text-white hover:bg-[#0056B3] transition-colors"
              onClick={onValidate}
              data-testid="DataTable-btn-validate"
            >
              {validateButtonLabel}
            </Button>
          )}

          {searchColumns && searchColumns.length > 0 && (
            <div className="relative max-w-sm w-full border overflow-hidden border-gray-300 rounded-full bg-white/50 text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-testid="DataTable-search-wrapper">
              <div className="absolute inset-y-0 right-0 flex items-center pl-1 pointer-events-none">
                <div className="w-10 h-10 text-indigo bg-gradient-to-br from-white/40 to-white/10 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md border border-white/30">
                  <Search className="w-5 h-5 text-[#007bff]" />
                </div>
              </div>
              <input
                type="text"
                placeholder="Search..."
                value={globalSearch || ""}
                onChange={(e) => {
                  onGlobalSearchChange?.(e.target.value);
                }}
                className="w-full pr-12 pl-4 py-2"
                data-testid="DataTable-global-search-input"
              />
            </div>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="border-gray-200 text-black hover:bg-gray-50 hover:text-gray-900"
              data-testid="DataTable-columns-dropdown-trigger"
            >
              Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white rounded-md shadow-lg border border-gray-200"
            data-testid="DataTable-columns-dropdown-content"
          >
            {table
              .getAllColumns()
              .filter((col) => col.getCanHide())
              .map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.id}
                  checked={col.getIsVisible()}
                  onCheckedChange={(value) => col.toggleVisibility(!!value)}
                  data-testid={`DataTable-column-checkbox-${col.id}`}
                >
                  {col.id}
                </DropdownMenuCheckboxItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Table */}
      <div className="rounded-xl border-2 border-gray-300 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300" data-testid="DataTable-table-container">
        <Table>
          <TableHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-gray-300">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="text-xs font-bold text-gray-800 tracking-wider h-10 px-3 py-2 border-r border-gray-300 last:border-r-0"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
                {(onEdit || onDelete || onDownload) && (
                  <th className="text-center px-3 py-2 font-bold text-black tracking-wider h-10 border-r-0">
                    Actions
                  </th>
                )}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody data-testid="DataTable-body">
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={
                    columns.length +
                    1 +
                    (onEdit || onDelete || onDownload ? 1 : 0)
                  }
                  className="h-24 text-center text-gray-500"
                >
                  <div className="flex items-center justify-center gap-2" data-testid="DataTable-loading-state">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                    Loading data...
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row, idx) => (
                <TableRow
                  key={row.id}
                  className={`transition-all duration-300 border-b border-gray-300 ${
                    idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                  }`}
                  data-testid={`DataTable-row-${row.id}`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className="text-sm text-gray-700 px-3 py-2 font-medium border-r border-gray-300 last:border-r-0"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}

                  {(onEdit || onDelete || onDownload) && (
                    <TableCell className="p-2 text-right border-r-0">
                      <div className="flex items-center justify-center gap-2" data-testid={`DataTable-actions-${row.id}`}>
                        {(row.original as any).isSoftDeleted ? (
                          recoveryActions?.(row.original) ?? (
                            <span className="text-slate-200 text-xs">—</span>
                          )
                        ) : (
                          <>
                            {onEdit && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="p-2 hover:bg-blue-50 text-blue-600 transition"
                                onClick={() => {
                                  onEdit(row.original);
                                  onEditClick?.(row.original);
                                }}
                                data-testid={`DataTable-btn-edit-${(row.original as any).id || row.id}`}
                              >
                                <Edit2 className="text-lg" />
                              </Button>
                            )}
                            {onDelete && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="p-2 hover:bg-red-50 text-red-600 transition"
                                onClick={() =>
                                  handleDeleteClick((row.original as any).id)
                                }
                                data-testid={`DataTable-btn-delete-${(row.original as any).id || row.id}`}
                              >
                                <Trash2 className="text-lg" />
                              </Button>
                            )}
                            {onDownload && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="p-2 hover:bg-green-50 text-green-600 transition"
                                onClick={() => onDownload(row.original)}
                                data-testid={`DataTable-btn-download-${(row.original as any).id || row.id}`}
                              >
                                <Download className="text-lg" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={
                    columns.length +
                    1 +
                    (onEdit || onDelete || onDownload ? 1 : 0)
                  }
                  className="h-24 text-center text-gray-500"
                  data-testid="DataTable-empty-state"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between pt-4" data-testid="DataTable-pagination">
        <div className="flex items-center gap-4">
          <Select
            value={String(pageSize)}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger
              className="w-[100px] bg-white border border-gray-300 text-black font-medium shadow-sm"
              data-testid="DataTable-page-size-trigger"
            >
              <SelectValue placeholder={pageSize} />
            </SelectTrigger>
            <SelectContent
              className="bg-white text-black shadow-lg"
              data-testid="DataTable-page-size-content"
            >
              {[5, 10, 20, 30, 40, 50].map((size) => (
                <SelectItem
                  key={size}
                  value={String(size)}
                  data-testid={`DataTable-page-size-item-${size}`}
                >
                  {size} rows
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div
            className="flex w-[100px] items-center justify-center text-sm text-gray-600"
            data-testid="DataTable-page-info"
          >
            Page {pageIndex + 1} of {totalPages}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={() => onPageChange(pageIndex - 1)}
            disabled={pageIndex === 0}
            variant="outline"
            size="sm"
            className="border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50"
            data-testid="DataTable-btn-prev"
          >
            Previous
          </Button>
          <Button
            onClick={() => onPageChange(pageIndex + 1)}
            disabled={pageIndex + 1 >= totalPages}
            variant="outline"
            size="sm"
            className="border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50"
            data-testid="DataTable-btn-next"
          >
            Next
          </Button>
        </div>
      </div>

      {/* Delete Modal */}
      {deleteDialogOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          data-testid="DataTable-delete-modal-overlay"
        >
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleCancelDelete}
          />
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 animate-in fade-in zoom-in duration-200"
            data-testid="DataTable-delete-modal-content"
          >
            <button
              onClick={handleCancelDelete}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="DataTable-delete-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h3
              className="text-xl font-bold text-gray-900 text-center mb-2"
              data-testid="DataTable-delete-modal-title"
            >
              Confirm Delete
            </h3>
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to delete this item? This action cannot be
              undone.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={handleCancelDelete}
                variant="outline"
                className="flex-1 border-gray-300 hover:bg-gray-50"
                data-testid="DataTable-delete-modal-btn-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                className="flex-1 bg-red-600 text-white hover:bg-red-700"
                data-testid="DataTable-delete-modal-btn-confirm"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;